// ONGLETS D'ÉCRAN — une page ne montre plus tous ses blocs d'un coup : un
// onglet à la fois, comme les filtres de la liste des études.
//
// L'onglet choisi est retenu par écran (préférence d'affichage, soumise au
// bandeau cookies). Un lien d'un bloc vers un autre (« Voir les insights »)
// change d'onglet avec `openTab(scope, tab)`.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { savePreference } from '../legal/cookieConsent';

export interface TabDef<T extends string> {
  id: T;
  label: string;
  /** Petit compteur à droite du libellé (absent : rien). */
  count?: number | null;
}

const EVENT = 'ro:open-tab';

// Un onglet à ouvrir sur l'écran SUIVANT (pas encore affiché) : par exemple
// l'onglet Participants de l'étude qu'on vient de créer. Lu une fois.
const preset = new Map<string, string>();

/** Le prochain affichage de l'écran `scope` s'ouvrira sur l'onglet `tab`. */
export function presetTab(scope: string, tab: string) {
  preset.set(scope, tab);
}

/** Ouvre l'onglet `tab` de l'écran `scope`, depuis n'importe quel bloc. */
export function openTab(scope: string, tab: string) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { scope, tab } }));
}

/**
 * L'onglet courant d'un écran. `scope` sert de clé de mémorisation (ex.
 * `dashboard`, `options`) ; un onglet mémorisé qui n'existe plus retombe sur
 * `fallback`.
 */
export function useTabs<T extends string>(scope: string, ids: readonly T[], fallback: T) {
  const key = `ro.tabs.${scope}`;
  const [tab, setTab] = useState<T>(() => {
    const wanted = preset.get(scope) as T | undefined;
    if (wanted && ids.includes(wanted)) {
      preset.delete(scope);
      return wanted;
    }
    try {
      const saved = localStorage.getItem(key) as T | null;
      return saved && ids.includes(saved) ? saved : fallback;
    } catch {
      return fallback;
    }
  });
  const choose = (t: T) => {
    setTab(t);
    savePreference(key, t);
  };
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ scope: string; tab: string }>).detail;
      if (d?.scope === scope && ids.includes(d.tab as T)) setTab(d.tab as T);
    };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
    // `ids` est recalculé à chaque rendu : seule sa valeur compte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, ids.join('|')]);
  // Un onglet devenu indisponible (droits, type d'étude) : on retombe.
  const current = ids.includes(tab) ? tab : fallback;
  return [current, choose] as const;
}

export function TabBar<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: TabDef<T>[];
  value: T;
  onChange: (t: T) => void;
  /** Nom de la barre pour les lecteurs d'écran. */
  label: string;
}) {
  // Sur téléphone, la barre défile : l'onglet ouvert vient dans la zone visible.
  const bar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = bar.current;
    const on = el?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!el || !on || el.scrollWidth <= el.clientWidth) return;
    el.scrollTo({ left: on.offsetLeft - (el.clientWidth - on.offsetWidth) / 2, behavior: 'smooth' });
  }, [value]);
  return (
    <div className="zy-tabs" role="tablist" aria-label={label} ref={bar}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          id={`tab-${t.id}`}
          aria-selected={value === t.id}
          className="zy-tab"
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.count !== undefined && t.count !== null ? <span className="zy-tab__n">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Le contenu de l'onglet ouvert. */
export function TabPanel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div role="tabpanel" aria-labelledby={`tab-${id}`} className="tab-panel">
      {children}
    </div>
  );
}
